import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { api } from '../../../services/api';
import { signInWithToken, mapAuthError } from '../../../services/auth';
import { DEFAULT_WORKSPACE_PATH } from '../../workspace/navigation';

/**
 * Support/dev tool: enter a Firebase UID (or email) + secret to sign in as that user.
 * Backend: POST /api/adminLogin
 */
const AdminLogin = () => {
  const navigate = useNavigate();
  const [uid, setUid] = useState('');
  const [secret, setSecret] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setPreview(null);
    setLoading(true);

    try {
      const result = await api.adminLogin({
        uid: uid.trim(),
        secret: secret.trim() || undefined,
      });

      if (result.error) {
        setError(result.error);
        setLoading(false);
        return;
      }

      const customToken = result.data?.customToken;
      if (!customToken) {
        setError('No custom token returned from server');
        setLoading(false);
        return;
      }

      setPreview(result.data.user || null);
      await signInWithToken(customToken);
      navigate(DEFAULT_WORKSPACE_PATH, { replace: true });
    } catch (err) {
      setError(mapAuthError(err) || err.message || 'Login failed');
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-sand-100 px-4 py-12 font-body text-ink-900 sm:px-6 lg:px-8">
      <div className="w-full max-w-md">
        <div className="mb-10 text-center">
          <Link to="/" className="group inline-flex items-center gap-3">
            <div className="h-11 w-11 overflow-hidden rounded-full ring-1 ring-ink-900/15 transition-transform group-hover:scale-105">
              <img src="/logo192.png" alt="" className="h-full w-full scale-110 object-cover" />
            </div>
            <span className="font-display text-2xl font-light tracking-tight text-ink-900">
              Spirara<span className="text-saffron-500">.</span>
            </span>
          </Link>
        </div>

        <div className="mb-8 text-center">
          <p className="text-xs uppercase tracking-[0.22em] text-saffron-600">Support</p>
          <h1 className="mt-2 font-display text-4xl font-light tracking-tight text-ink-900">
            Admin login
          </h1>
          <p className="mt-3 text-sm leading-relaxed text-ink-700/80">
            Sign in as any Firebase user with their UID (or email).
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="space-y-5 bg-sand-50 p-8 shadow-[0_30px_60px_-30px_rgba(6,18,15,0.45)] ring-1 ring-ink-900/10 sm:p-10"
        >
          {error && (
            <div className="border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800">
              {error}
            </div>
          )}

          <div>
            <label
              htmlFor="admin-uid"
              className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-ink-700"
            >
              Firebase UID or email
            </label>
            <input
              id="admin-uid"
              required
              autoComplete="off"
              spellCheck={false}
              placeholder="oeAzZGWiSTbmEt1OpMmtUcOXxX22"
              value={uid}
              onChange={(e) => setUid(e.target.value)}
              className="w-full bg-white px-4 py-3 font-mono text-sm ring-1 ring-inset ring-ink-900/15 focus:outline-none focus:ring-2 focus:ring-saffron-500"
            />
          </div>

          <div>
            <label
              htmlFor="admin-secret"
              className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-ink-700"
            >
              Admin secret
            </label>
            <input
              id="admin-secret"
              type="password"
              autoComplete="off"
              placeholder="ADMIN_LOGIN_SECRET"
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              className="w-full bg-white px-4 py-3 text-sm ring-1 ring-inset ring-ink-900/15 focus:outline-none focus:ring-2 focus:ring-saffron-500"
            />
            <p className="mt-2 text-xs text-ink-600/70">
              Must match <code className="text-ink-800">ADMIN_LOGIN_SECRET</code> on the server.
            </p>
          </div>

          {preview && (
            <p className="text-xs text-ink-600/70">
              Signing in as {preview.email || preview.uid}…
            </p>
          )}

          <button
            type="submit"
            disabled={loading || !uid.trim()}
            className="group inline-flex w-full items-center justify-center gap-2 bg-saffron-500 px-6 py-4 text-sm font-semibold uppercase tracking-[0.12em] text-ink-900 transition-colors hover:bg-saffron-400 disabled:opacity-50"
          >
            {loading ? 'Signing in…' : 'Log in as user'}
            {!loading && (
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AdminLogin;
